import React, { useCallback, useMemo, useState } from 'react';
import AutosizeInput from 'react-input-autosize';
import styled from 'styled-components';
import { LFC } from '../../../Legacy';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { ObjectManager } from '../../../concepts/objects';
import { Icon } from '../../../concepts/resources/Icon';
import { Colors, Float, Grid, Prompt, remInPx } from '../../../ui';
import { ColorButton } from '../../../ui/ColorButton';
import { DropButton } from '../../../ui/DropButton';
import { BetterPopArrow } from '../../../ui/PopArrow';
import { useResolve } from '../../Container';
import { Physical } from '../../canvas/entities/Physical';
import { toolEquip, toolSetParam } from '../../canvas/tooling/ToolCommands';
import { useComponentObserver } from '../../canvas/visualization/ComponentObserver';
import { PopoutWidget } from '../../canvas/workspace/widgets/WidgetInterface';
import { Divider, Flexer } from '../../dashboard/elements/LayoutElements';
import { Tag } from './Tag';
import { tagsDelete, tagsToggleEdit } from './TaggingCommands';

const XAutosizeInput = AutosizeInput as any;

const Mount = styled.div`
    position: relative;
    height: 0;
    white-space: nowrap;

    & > * {
        position: absolute;
        transform: translate(-50%, -100%) translateY(-1rem);
    }
`;

const Frame: LFC<{ color: string }> = ({ color, children }) => {
    return <Float inline shadow="xl">
        <Flexer flex="column" gap="1" border={`solid 2px ${color}`} borderRadius={.375} padding="1">
            {children}
        </Flexer>
        <BetterPopArrow placement="top" color="#fff" border={[2, color]} size="1.5" style={{ left: '50%' }} />
    </Float>;
};

const LabelCell = styled.div`
    display: flex;
    align-items: center;
    height: 1.75rem;
    padding-right: .25rem;
    color: ${Colors.textLighter};
`;

const ValueCell = styled.div`
    display: flex;
    align-items: center;
    height: 1.75rem;
    color: ${Colors.text};
`;

const DropInputFrame = styled.div`
    display: inline-flex;
    input {
        color: ${Colors.text};
    }
`;

const ValueInput = ({ tag, prop, upper, autoFocus }: { tag: Tag, prop: string, upper?: boolean, autoFocus?: boolean }) => {

    const objects = useResolve(ObjectManager);
    const ticker = useComponentObserver(tag);
    const value = useMemo(() => tag.get(prop), [ticker]);
    const [editing, setEditing] = useState(false);

    const onChange = useCallback((e: any) => {
        if (!tag.alive) return;
        objects.update(u => {
            u.rolling(`edit:${tag.id}`);
            tag.set(prop, (e.target.value || '').toUpperCase());
        });
    }, [tag]);

    const onFocus = useCallback((e: any) => {
        setEditing(true);
        e.target.select();
    }, []);
    const onBlur = useCallback(() => {
        setEditing(false);
    }, [value]);

    // return <InlineEditTextInput initialValue={value} onChange={onApply} {...{ upper, autoFocus }} />

    return <DropButton active={editing} padding=".125 .375" overflow="0">
        <DropInputFrame>
            <XAutosizeInput type="text" autoComplete="false" value={value} minWidth={remInPx(8)} maxLength={128}
                {...{ autoFocus, onFocus, onBlur, onChange }} />
        </DropInputFrame>
    </DropButton>;
};

const TagColorInput = ({ tag }: { tag: Tag }) => {

    const objects = useResolve(ObjectManager);
    const ticker = useComponentObserver(tag);
    const value = useMemo(() => tag.color, [ticker]);

    const onChange = useCallback((newValue: string) => {
        if (value === newValue) return;
        if (!tag.alive) return;
        objects.update(u => {
            u.rolling(`tag/${tag.id}`);
            tag.color = newValue;
        });
    }, [tag]);

    return <Flexer margin="0 .25">
        <ColorButton groups value={value} onChange={onChange} />
    </Flexer>;
};

const TagActionButton = styled(DropButton)`
    color: ${Colors.textLight};

    &:hover {
        color: ${Colors.textLight};
    }
`;

export const TagWidget: LFC<{ widget: PopoutWidget }> = ({ widget }) => {
    const subject = widget.subject as Tag;
    const dispatch = useResolve(CommandDispatcher);
    const onClose = useCallback(() => dispatch(tagsToggleEdit), []);

    useComponentObserver(subject);

    const onEquip = useCallback(() => {
        dispatch(toolSetParam, ['tagTool', 'name', subject.name]);
        dispatch(toolSetParam, ['tagTool', 'value', subject.value]);
        dispatch(toolSetParam, ['tagTool', 'symbol', subject.symbol]);
        dispatch(toolSetParam, ['tagTool', 'color', subject.color]);
        dispatch(toolEquip, ['tagTool']);
        onClose();
    }, [subject, onClose]);

    const onDelete = useCallback(() => {
        const parent = subject.parent?.as(Physical);
        if (!parent) return;
        dispatch(tagsDelete, [subject.name, [parent.id]]);
        onClose();
    }, [subject, onClose]);

    if (!subject?.alive) return null;

    return <Prompt ignoreEnter onAction={onClose}>
        <Mount>
            <Frame color={subject.color}>
                <Grid columns="auto auto" columnGap=".5" rowGap=".25">
                    <LabelCell>Name:</LabelCell>
                    <ValueCell>
                        <ValueInput tag={subject} prop="name" autoFocus={!subject.name} upper />
                    </ValueCell>
                    <LabelCell>Value:</LabelCell>
                    <ValueCell>
                        <ValueInput tag={subject} prop="value" autoFocus={!!subject.name && !subject.value} upper />
                    </ValueCell>
                    <LabelCell>Symbol:</LabelCell>
                    <ValueCell>
                        <ValueInput tag={subject} prop="symbol" />
                    </ValueCell>
                    <LabelCell>Color:</LabelCell>
                    <ValueCell>
                        <TagColorInput tag={subject} />
                    </ValueCell>
                </Grid>
                <Divider mode="horizontal" />
                <Grid columns="auto auto" gap=".25">
                    <TagActionButton onClick={onEquip}>
                        <Icon size="1.25" src="deqo://icons/hero/tag" />
                        <span>Equip</span>
                    </TagActionButton>
                    <TagActionButton onClick={onDelete}>
                        <Icon size="1.25" src="deqo://icons/hero/trash" />
                        <span>Delete</span>
                    </TagActionButton>
                </Grid>
            </Frame>
        </Mount>
    </Prompt>;
};
